import { Modal, Form, Input, InputNumber, Upload, Button, message, Select, Divider, Tag } from 'antd';
import { UploadOutlined, CloseOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Product, useProducts, CreateProductInput, UpdateProductInput, ProductVariant } from '@hooks';
import type { UploadFile } from 'antd/es/upload/interface';

const { TextArea } = Input;
const { Option } = Select;

interface ProductFormModalProps {
  open: boolean;
  onCancel: () => void;
  editingProduct: Product | null;
  onSuccess: () => void;
}

interface DocumentWithMetadata {
  file: File;
  name: string;
  description: string;
  existingDocId?: string; // ID of existing document (if editing)
  existingDocUrl?: string; // URL of existing document (if editing)
}

interface VariantFormData {
  sku: string;
  size: string;
  chainSize?: string | null;
  price: number;
  stockQuantity: number;
}

const PREDEFINED_CATEGORIES = [
  
  'Heavy Machinery',
  'Parts & Accessories',
  'Safety Gear',
  'Tools',
  'Trackless',
  'Trucks and Busses'
];

export const ProductFormModal = ({ open, onCancel, editingProduct, onSuccess }: ProductFormModalProps) => {
  const [form] = Form.useForm();
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [documentFiles, setDocumentFiles] = useState<DocumentWithMetadata[]>([]);
  const [variants, setVariants] = useState<VariantFormData[]>([]);
  const [sizes, setSizes] = useState<string[]>([]);
  const [chainSizes, setChainSizes] = useState<string[]>([]);
  const [newSize, setNewSize] = useState('');
  const [newChainSize, setNewChainSize] = useState('');
  const { addProduct, updateProduct } = useProducts();

  const isEditing = !!editingProduct;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      if (editingProduct) {
        form.setFieldsValue({
          name: editingProduct.name,
          description: editingProduct.description,
          imageUrl: editingProduct.imageUrl,
          videoUrl: editingProduct.videoUrl,
          category: editingProduct.category,
        });
        
        // Set image previews if editing and product has images
        if (editingProduct.images && editingProduct.images.length > 0) {
          const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'modlink-production';
          const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
          const previews = editingProduct.images.map(img => 
            img.startsWith('http') ? img : `https://${bucketName}.s3.${region}.amazonaws.com/${img}`
          );
          setImagePreviews(previews);
        } else if (editingProduct.imageUrl) {
          // Fallback to legacy imageUrl
          const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'modlink-production';
          const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
          const fullImageUrl = editingProduct.imageUrl.startsWith('http') 
            ? editingProduct.imageUrl 
            : `https://${bucketName}.s3.${region}.amazonaws.com/${editingProduct.imageUrl}`;
          setImagePreviews([fullImageUrl]);
        }

        // Set variants if editing
        if (editingProduct.variants && editingProduct.variants.length > 0) {
          setVariants(editingProduct.variants.map((v: ProductVariant) => ({
            sku: v.sku,
            size: v.size,
            chainSize: v.chainSize || null,
            price: Number(v.price),
            stockQuantity: v.stockQuantity,
          })));
          
          // Extract unique sizes and chain sizes
          const uniqueSizes = [...new Set(editingProduct.variants.map((v: ProductVariant) => v.size))];
          const uniqueChainSizes = [...new Set(editingProduct.variants.map((v: ProductVariant) => v.chainSize).filter(Boolean))] as string[];
          setSizes(uniqueSizes);
          setChainSizes(uniqueChainSizes);
        } else {
          setVariants([]);
          setSizes(editingProduct.sizes || []);
          setChainSizes(editingProduct.chainSizes || []);
        }

        // Load existing documents if editing and product has documents
        if (editingProduct.documents && editingProduct.documents.length > 0) {
          // Convert existing documents to DocumentWithMetadata format
          const existingDocs = editingProduct.documents.map(doc => ({
            file: new File([], doc.fileUrl, { type: 'application/pdf' }), // Placeholder file with URL as name
            name: doc.name,
            description: doc.description || '',
            existingDocId: doc.id, // Mark as existing document
            existingDocUrl: doc.fileUrl, // Store the URL for display
          }));
          setDocumentFiles(existingDocs as any);
        } else {
          setDocumentFiles([]);
        }
      } else {
        // Reset with default values for new product
        form.setFieldsValue({
          name: '',
          description: '',
          imageUrl: '',
          videoUrl: '',
          category: '',
        });
        setImagePreviews([]);
        setVariants([]);
        setSizes([]);
        setChainSizes([]);
      }
      setImageFiles([]);
      setDocumentFiles([]);
      setNewSize('');
      setNewChainSize('');
    }
  }, [open, editingProduct, form]);

  const handleImagesChange = (info: any) => {
    const files = info.fileList.map((file: UploadFile) => file.originFileObj).filter(Boolean) as File[];
    
    if (files.length > 5) {
      message.error('You can only upload up to 5 images!');
      return;
    }

    // Validate file types
    const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      message.error('You can only upload image files!');
      return;
    }

    // Validate file sizes (5MB each)
    const largeFiles = files.filter(file => file.size / 1024 / 1024 > 5);
    if (largeFiles.length > 0) {
      message.error('Each image must be smaller than 5MB!');
      return;
    }

    setImageFiles(files);
    
    // Create previews
    const previews: string[] = [];
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        previews.push(e.target?.result as string);
        if (previews.length === files.length) {
          setImagePreviews(previews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDocumentAdd = () => {
    if (documentFiles.length >= 3) {
      message.error('You can only add up to 3 documents!');
      return;
    }
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        if (file.type !== 'application/pdf') {
          message.error('Only PDF files are allowed!');
          return;
        }
        if (file.size / 1024 / 1024 > 10) {
          message.error('PDF file must be smaller than 10MB!');
          return;
        }
        
        setDocumentFiles(prev => [...prev, {
          file,
          name: file.name.replace('.pdf', ''),
          description: ''
        }]);
      }
    };
    input.click();
  };

  const handleDocumentRemove = (index: number) => {
    setDocumentFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDocumentMetadataChange = (index: number, field: 'name' | 'description', value: string) => {
    setDocumentFiles(prev => prev.map((doc, i) => 
      i === index ? { ...doc, [field]: value } : doc
    ));
  };

  // Variant management functions
  const handleAddSize = () => {
    if (newSize.trim() && !sizes.includes(newSize.trim())) {
      setSizes(prev => [...prev, newSize.trim()]);
      setNewSize('');
    }
  };

  const handleRemoveSize = (size: string) => {
    setSizes(prev => prev.filter(s => s !== size));
    // Remove variants with this size
    setVariants(prev => prev.filter(v => v.size !== size));
  };

  const handleAddChainSize = () => {
    if (newChainSize.trim() && !chainSizes.includes(newChainSize.trim())) {
      setChainSizes(prev => [...prev, newChainSize.trim()]);
      setNewChainSize('');
    }
  };

  const handleRemoveChainSize = (chainSize: string) => {
    const newChainSizes = chainSizes.filter(s => s !== chainSize);
    setChainSizes(newChainSizes);
    // Remove variants with this chain size or set chainSize to null if no chain sizes left
    if (newChainSizes.length === 0) {
      // Last chain size being removed - set all chainSizes to null
      setVariants(prev => prev.map(v => ({ ...v, chainSize: null })));
    } else {
      setVariants(prev => prev.filter(v => v.chainSize !== chainSize));
    }
  };

  const handleAddVariant = () => {
    if (sizes.length === 0) {
      message.warning('Please add at least one size first');
      return;
    }
    
    // If no chain sizes, find first size that doesn't have a variant
    if (chainSizes.length === 0) {
      for (const size of sizes) {
        const exists = variants.some(v => v.size === size && !v.chainSize);
        if (!exists) {
          setVariants(prev => [...prev, {
            sku: '',
            size,
            chainSize: null,
            price: 0,
            stockQuantity: 0,
          }]);
          return;
        }
      }
    } else {
      // Find first combination that doesn't exist
      for (const size of sizes) {
        for (const chainSize of chainSizes) {
          const exists = variants.some(v => v.size === size && v.chainSize === chainSize);
          if (!exists) {
            setVariants(prev => [...prev, {
              sku: '',
              size,
              chainSize,
              price: 0,
              stockQuantity: 0,
            }]);
            return;
          }
        }
      }
    }
    
    message.info('All possible combinations already exist');
  };

  const handleRemoveVariant = (index: number) => {
    setVariants(prev => prev.filter((_, i) => i !== index));
  };

  const handleVariantChange = (index: number, field: keyof VariantFormData, value: any) => {
    setVariants(prev => prev.map((variant, i) => 
      i === index ? { ...variant, [field]: value } : variant
    ));
  };

  const generateAllVariants = () => {
    if (sizes.length === 0) {
      message.warning('Please add sizes first');
      return;
    }

    const newVariants: VariantFormData[] = [];
    
    if (chainSizes.length === 0) {
      // No chain sizes - create one variant per size
      for (const size of sizes) {
        const existing = variants.find(v => v.size === size && !v.chainSize);
        newVariants.push(existing || {
          sku: '',
          size,
          chainSize: null,
          price: 0,
          stockQuantity: 0,
        });
      }
    } else {
      // Has chain sizes - create combinations
      for (const size of sizes) {
        for (const chainSize of chainSizes) {
          const existing = variants.find(v => v.size === size && v.chainSize === chainSize);
          newVariants.push(existing || {
            sku: '',
            size,
            chainSize,
            price: 0,
            stockQuantity: 0,
          });
        }
      }
    }
    
    setVariants(newVariants);
    message.success(`Generated ${newVariants.length} variants`);
  };

  const handleSubmit = async (values: any) => {
    try {
      // Validate that at least one size exists
      if (sizes.length === 0) {
        message.error('Please add at least one size');
        return;
      }

      // Validate that all sizes have variants with prices
      if (variants.length === 0) {
        message.error('Please generate variants and set prices');
        return;
      }

      // Validate that all variants have SKU
      const variantsWithoutSku = variants.filter(v => !v.sku || v.sku.trim() === '');
      if (variantsWithoutSku.length > 0) {
        message.error('Please fill in SKU for all variants');
        return;
      }

      const productData: CreateProductInput | UpdateProductInput = {
        name: values.name,
        description: values.description,
        videoUrl: values.videoUrl || '',
        category: values.category,
        sizes: Array.isArray(sizes) ? sizes : [],
        chainSizes: Array.isArray(chainSizes) ? chainSizes : [],
        variants: Array.isArray(variants) ? variants.map(v => ({
          sku: v.sku,
          size: v.size,
          chainSize: v.chainSize || null,
          price: Number(v.price),
          stockQuantity: Number(v.stockQuantity),
        })) : [],
      };

      // Add images if uploaded
      if (imageFiles.length > 0) {
        productData.images = imageFiles;
      }

      // Add documents if uploaded
      if (documentFiles.length > 0) {
        productData.documents = documentFiles.map(doc => doc.file);
        productData.documentsMetadata = documentFiles.map(doc => ({
          name: doc.name,
          description: doc.description
        }));
      }

      if (isEditing && editingProduct) {
        await updateProduct.mutateAsync({
          id: editingProduct.id,
          data: productData as UpdateProductInput,
        });
      } else {
        await addProduct.mutateAsync(productData as CreateProductInput);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setImageFiles([]);
    setImagePreviews([]);
    setDocumentFiles([]);
    setVariants([]);
    setSizes([]);
    setChainSizes([]);
    setNewSize('');
    setNewChainSize('');
    onCancel();
  };

  return (
    <Modal
      title={
        <span className="font-orator text-orangicide text-xl">
          {isEditing ? 'EDIT PRODUCT' : 'ADD NEW PRODUCT'}
        </span>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={700}
      styles={{
        mask: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.7)' },
        content: { backgroundColor: '#181717', border: '1px solid #3f3f46', borderRadius: '0px', maxHeight: '90vh', overflow: 'auto' },
        header: { backgroundColor: '#181717', borderBottom: '1px solid #3f3f46' },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        className="mt-6"
      >
        <Form.Item
          label={<span className="font-orator text-orangicide">PRODUCT NAME</span>}
          name="name"
          rules={[
            { required: true, message: 'Please enter product name' },
            { min: 1, max: 255, message: 'Name must be between 1 and 255 characters' }
          ]}
        >
          <Input 
            className="bg-zinc-800 font-orator border-zinc-700 text-white" 
            placeholder="Enter product name"
          />
        </Form.Item>

        <Form.Item
          label={<span className="font-orator text-orangicide">DESCRIPTION</span>}
          name="description"
          rules={[
            { required: true, message: 'Please enter product description' },
            { max: 5000, message: 'Description must be less than 5000 characters' }
          ]}
        >
          <TextArea
            rows={4}
            className="bg-zinc-800 font-orator border-zinc-700 text-white"
            placeholder="Enter product description"
          />
        </Form.Item>


        <Form.Item
          label={<span className="font-orator text-orangicide">CATEGORY</span>}
          name="category"
        >
          <Select 
            className="font-orator"
            placeholder="Select a category"
            allowClear
          >
            {PREDEFINED_CATEGORIES.map(cat => (
              <Option key={cat} value={cat}>{cat}</Option>
            ))}
          </Select>
        </Form.Item>

        {/* Product Sizes and Variants Section */}
        <Divider className="border-zinc-700">
          <span className="font-orator text-orangicide">SIZES & PRICING (Required)</span>
        </Divider>

        <div className="bg-zinc-800 p-4 border border-zinc-700 mb-4">
          <p className="font-orator text-zinc-300 text-sm mb-4">
            Add sizes for this product. Chain sizes are optional. Price and stock are defined per variant.
          </p>

          {/* Sizes */}
          <div className="mb-4">
            <label className="font-orator text-orangicide block mb-2">SIZES <span className="text-red-500">*</span></label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newSize}
                onChange={(e) => setNewSize(e.target.value)}
                placeholder="e.g., 20.5-25"
                className="!bg-zinc-700 font-orator border-zinc-700 text-white flex-1 !rounded-none"
                onPressEnter={handleAddSize}
              />
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddSize}
                className="btn-orangicide font-orator"
              >
                Add
              </Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {sizes.map((size, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => handleRemoveSize(size)}
                  className="font-orator bg-zinc-700 border-zinc-600 text-black"
                >
                  {size}
                </Tag>
              ))}
            </div>
            {sizes.length === 0 && (
              <p className="font-orator text-red-400 text-xs mt-2">At least one size is required</p>
            )}
          </div>

          {/* Chain Sizes */}
          <div className="mb-4">
            <label className="font-orator text-orangicide block mb-2">CHAIN SIZES <span className="text-zinc-500">(Optional)</span></label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newChainSize}
                onChange={(e) => setNewChainSize(e.target.value)}
                placeholder="e.g., 11mm"
                className="!bg-zinc-700 font-orator border-zinc-700 text-white flex-1 !rounded-none"
                onPressEnter={handleAddChainSize}
              />
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddChainSize}
                className="btn-orangicide font-orator"
              >
                Add
              </Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {chainSizes.map((chainSize, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => handleRemoveChainSize(chainSize)}
                  className="font-orator bg-zinc-700 border-zinc-600 text-black"
                >
                  {chainSize}
                </Tag>
              ))}
            </div>
            {chainSizes.length === 0 && (
              <p className="font-orator text-zinc-500 text-xs mt-2">No chain sizes - variants will be created per size only</p>
            )}
          </div>

          {/* Generate Variants Button */}
          {sizes.length > 0 && (
            <div className="mb-4">
              <Button
                onClick={generateAllVariants}
                className="btn-orangicide font-orator"
              >
                Generate All Variants ({chainSizes.length > 0 ? sizes.length * chainSizes.length : sizes.length} {chainSizes.length > 0 ? 'combinations' : 'sizes'})
              </Button>
            </div>
          )}

          {/* Variants Table */}
          {variants.length > 0 && (
            <div className="mt-4">
              <label className="font-orator text-orangicide block mb-2">VARIANT PRICES & STOCK</label>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-zinc-600">
                      <th className="font-orator text-orangicide text-left py-2 px-2 text-sm">SKU</th>
                      <th className="font-orator text-orangicide text-left py-2 px-2 text-sm">SIZE</th>
                      {chainSizes.length > 0 && (
                        <th className="font-orator text-orangicide text-left py-2 px-2 text-sm">CHAIN SIZE</th>
                      )}
                      <th className="font-orator text-orangicide text-left py-2 px-2 text-sm">PRICE ($)</th>
                      <th className="font-orator text-orangicide text-left py-2 px-2 text-sm">STOCK</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.map((variant, index) => (
                      <tr key={index} className="border-b border-zinc-700">
                        <td className="py-2 px-1">
                          <Input
                            value={variant.sku}
                            onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                            placeholder="SKU"
                            className="!bg-zinc-700 font-orator !border !border-solid !border-zinc-600 !text-white placeholder:!text-zinc-500 !rounded-none"
                            size="small"
                          />
                        </td>
                        <td className="py-2 px-1">
                          <Select
                            value={variant.size}
                            onChange={(value) => handleVariantChange(index, 'size', value)}
                            className="w-full font-orator variant-select-dark"
                            size="small"
                            popupClassName="!bg-zinc-800 [&_.ant-select-item]:!text-white [&_.ant-select-item-option-selected]:!bg-zinc-700 [&_.ant-select-item-option-active]:!bg-zinc-600"
                          >
                            {sizes.map(size => (
                              <Option key={size} value={size}>{size}</Option>
                            ))}
                          </Select>
                        </td>
                        {chainSizes.length > 0 && (
                          <td className="py-2 px-1">
                            <Select
                              value={variant.chainSize || undefined}
                              onChange={(value) => handleVariantChange(index, 'chainSize', value)}
                              className="w-full font-orator variant-select-dark"
                              size="small"
                              placeholder="Select"
                              popupClassName="!bg-zinc-800 [&_.ant-select-item]:!text-white [&_.ant-select-item-option-selected]:!bg-zinc-700 [&_.ant-select-item-option-active]:!bg-zinc-600"
                            >
                              {chainSizes.map(chainSize => (
                                <Option key={chainSize} value={chainSize}>{chainSize}</Option>
                              ))}
                            </Select>
                          </td>
                        )}
                        <td className="py-2 px-1">
                          <InputNumber
                            value={variant.price}
                            onChange={(val) => handleVariantChange(index, 'price', val || 0)}
                            min={0}
                            step={0.01}
                            precision={2}
                            className="w-full font-orator variant-input-dark"
                            size="small"
                          />
                        </td>
                        <td className="py-2 px-1">
                          <InputNumber
                            value={variant.stockQuantity}
                            onChange={(val) => handleVariantChange(index, 'stockQuantity', val || 0)}
                            min={0}
                            step={1}
                            precision={0}
                            className="w-full font-orator variant-input-dark"
                            size="small"
                          />
                        </td>
                        <td className="py-2 px-1">
                          <Button
                            type="text"
                            icon={<DeleteOutlined className="text-orangicide hover:text-red-400" />}
                            onClick={() => handleRemoveVariant(index)}
                            size="small"
                            className="!text-orangicide hover:!text-red-400"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddVariant}
                className="mt-2 btn-orangicide font-orator"
                size="small"
              >
                Add Variant
              </Button>
            </div>
          )}
          
          {/* Show message if no variants generated */}
          {sizes.length > 0 && variants.length === 0 && (
            <div className="mt-4 p-3 border border-orangicide/50 bg-orangicide/10">
              <p className="font-orator text-orangicide text-sm">
                Click "Generate All Variants" to create price entries for each size.
              </p>
            </div>
          )}
        </div>

        <Form.Item
          label={<span className="font-orator text-orangicide">VIDEO URL (YouTube/Vimeo Embed - Optional)</span>}
          name="videoUrl"
        >
          <Input 
            className="bg-zinc-800 font-orator border-zinc-700 text-white" 
            placeholder="e.g., https://www.youtube.com/embed/..."
          />
        </Form.Item>

        <div className="mb-6">
          <label className="font-orator text-white block mb-2">PRODUCT IMAGES (Up to 5)</label>
          <Upload
            beforeUpload={() => false}
            onChange={handleImagesChange}
            multiple
            maxCount={5}
            accept="image/*"
            fileList={imageFiles.map((file, index) => ({
              uid: `${index}`,
              name: file.name,
              status: 'done' as const,
              originFileObj: file as any
            }))}
            className="inline-block"
          >
            <Button 
              icon={<UploadOutlined />}
              className="btn-orangicide font-orator"
            >
              Select Images (Max 5)
            </Button>
          </Upload>
          
          {imagePreviews.length > 0 && (
            <div className="mt-4">
              <p className="font-orator text-white mb-2">Image Previews:</p>
              <div className="grid grid-cols-5 gap-2">
                {imagePreviews.map((preview, index) => (
                  <img 
                    key={index}
                    src={preview} 
                    alt={`Preview ${index + 1}`} 
                    className="w-full h-24 object-cover border border-zinc-700"
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mb-6">
          <label className="font-orator text-white block mb-2">TECHNICAL DOCUMENTS (PDFs - Up to 3)</label>
          <Button 
            onClick={handleDocumentAdd}
            icon={<UploadOutlined />}
            className="btn-orangicide font-orator"
            disabled={documentFiles.length >= 3}
          >
            Add Document ({documentFiles.length}/3)
          </Button>

          {documentFiles.length > 0 && (
            <div className="space-y-3 mt-4">
              {documentFiles.map((doc, index) => (
                <div key={index} className="bg-zinc-800 p-4 border border-zinc-700">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-orator text-white text-sm">
                      {doc.existingDocId ? `${doc.name}.pdf` : doc.file.name}
                      {doc.existingDocId && (
                        <span className="ml-2 text-xs text-orangicide">(Existing)</span>
                      )}
                    </span>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => handleDocumentRemove(index)}
                      className="text-red-500 hover:text-red-400"
                    />
                  </div>
                  <Input
                    placeholder="Document Name"
                    value={doc.name}
                    onChange={(e) => handleDocumentMetadataChange(index, 'name', e.target.value)}
                    className="bg-zinc-900 font-orator border-zinc-700 text-white mb-2"
                  />
                  <TextArea
                    rows={2}
                    placeholder="Document Description (Optional)"
                    value={doc.description}
                    onChange={(e) => handleDocumentMetadataChange(index, 'description', e.target.value)}
                    className="bg-zinc-900 font-orator border-zinc-700 text-white"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-4 pt-6 border-t border-zinc-700">
          <Button
            onClick={handleCancel}
            className="btn-orangicide font-orator text-black"
          >
            CANCEL
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={addProduct.isPending || updateProduct.isPending}
            className="btn-orangicide font-orator text-black"
          >
            {isEditing ? 'UPDATE PRODUCT' : 'CREATE PRODUCT'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
